import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { WelcomeTemplateEntity } from '../../../../../types';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import { useStore } from '../../../state/zustand-store';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import {
  bulkDeleteWelcomeTemplates,
  UserKeys,
} from '../../../../api/users-api';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { Messages } from '../../../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteWelcomeTemplateDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedWelcomeTemplates: WelcomeTemplateEntity[];
  changeLogRoles: string[];
}

export default function DeleteWelcomeTemplateDialog({
  open,
  handleClose,
  selectedWelcomeTemplates,
  changeLogRoles,
}: DeleteWelcomeTemplateDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();

  const moreThanOneSelection = selectedWelcomeTemplates.length > 1;

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      return bulkDeleteWelcomeTemplates(
        selectedWelcomeTemplates,
        changeLogMessage
      );
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted(
          `Welcome Template${moreThanOneSelection ? 's' : ''}`
        ),
      });

      queryClient.setQueryData(
        [UserKeys.EmailTemplates],
        (oldData: { results: WelcomeTemplateEntity[] } | undefined) => {
          if (oldData) {
            return {
              ...oldData,
              results: oldData.results.filter(
                (template) =>
                  !selectedWelcomeTemplates.some((t) => t.id === template.id)
              ),
            };
          }
          return oldData;
        }
      );

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          `welcome template${moreThanOneSelection ? 's' : ''}`
        ),
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.settings.delete_welcome_template, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.settings,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.DeleteWelcomeTemplate.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Delete Welcome Template${moreThanOneSelection ? 's' : ''}`}
      dialogContent={
        <Box>
          <Typography sx={{ mb: 2 }}>
            {selectedWelcomeTemplates.length > 1 ? (
              <>
                Do you wish to delete{' '}
                <strong>{selectedWelcomeTemplates.length}</strong> templates?
              </>
            ) : (
              <>
                Do you wish to delete the welcome template{' '}
                <strong>{selectedWelcomeTemplates[0]?.templateName}</strong>?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: `Delete Template${moreThanOneSelection ? 's' : ''}`,
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
